import React from "react";
import { Link } from "react-router-dom";

const SITE_URL = "https://betweenthenews.com";

// Terms of Use / end-user licence agreement.
//
// App Store Review Guideline 1.2 requires an app with user-generated content
// to bind posters to terms with "no tolerance for objectionable content or
// abusive users". Section 4 is that clause — it is quoted in the App Review
// notes, so don't soften it without updating those.
function TermsOfUse({ onBack }) {
  return (
    <div className="app" style={{ padding: 20, paddingBottom: 100 }}>
      <title>Terms of Use — Between the News</title>
      <meta
        name="description"
        content="The rules for using Between the News: our zero-tolerance policy on objectionable content and abusive users, how comments are moderated, and how to report or block."
      />
      <link rel="canonical" href={`${SITE_URL}/terms/`} />
      {onBack && (
        <button className="detail-back" onClick={onBack}>
          {"←"} Back
        </button>
      )}

      <div style={{ maxWidth: 600, margin: "0 auto" }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, marginBottom: 8 }}>Terms of Use</h1>
        <p style={{ fontSize: 12, color: "#6b6b80", marginBottom: 24 }}>
          Last updated: September 1, 2026
        </p>

        <div style={{ fontSize: 14, lineHeight: 1.8, color: "#34343f" }}>
          <Section title="1. Agreement">
            Between the News ("we", "our", "us") publishes media criticism on
            betweenthenews.com and in the Between the News mobile app (together, the
            "Service"). By using the Service — and in particular by posting a comment or a
            headline suggestion — you agree to these Terms of Use. If you do not agree,
            please do not post.
          </Section>

          <Section title="2. What the Service is">
            We do not report the news. We analyse how the news was reported: the wording of
            headlines, what was emphasised and what was buried, and how different outlets
            framed the same event. Our analyses are opinion and criticism, supported by
            quotations from the coverage we are criticising. Every quotation is checked
            against the outlet's own published wording. If we get something wrong, write to{" "}
            <a href="mailto:corrections@betweenthenews.com">corrections@betweenthenews.com</a>{" "}
            and we will correct it visibly in the piece.
          </Section>

          <Section title="3. Who may use the Service">
            The Service is rated 13+ and is intended for teenage and adult readers. It is not
            directed at children, no account or registration is required, and we do not
            knowingly collect information from children under 13.
          </Section>

          <Section title="4. Zero tolerance for objectionable content and abusive users">
            <strong>
              There is no tolerance for objectionable content or abusive behaviour on this
              Service.
            </strong>{" "}
            When you post a comment or a suggestion, you agree not to submit anything that:
            <ul style={{ paddingLeft: 20, marginTop: 8 }}>
              <li>is hateful, or attacks or demeans a person or group on the basis of race, ethnicity, national origin, religion, gender, gender identity, sexual orientation, disability or age;</li>
              <li>harasses, bullies, threatens or incites violence against anyone;</li>
              <li>is obscene, pornographic or sexually explicit;</li>
              <li>is defamatory, knowingly false, or impersonates another person;</li>
              <li>reveals private information about anyone (doxxing);</li>
              <li>promotes self-harm or illegal activity;</li>
              <li>is spam, advertising, or an attempt to manipulate votes; or</li>
              <li>infringes anyone's copyright or other rights.</li>
            </ul>
            <br />
            Content that breaks these rules is removed, and users who post it lose access to
            posting. We do not warn twice for slurs, threats or sexual content.
          </Section>

          <Section title="5. How moderation works">
            Three mechanisms operate together:
            <ul style={{ paddingLeft: 20, marginTop: 8 }}>
              <li>
                <strong>Filtering.</strong> Comments and suggestions are screened before they
                are posted; text containing slurs, sexual content, threats or spam is
                rejected and never published.
              </li>
              <li>
                <strong>Reporting.</strong> Every comment carries a{" "}
                <strong>Report</strong> control. Reports go to our editorial team and are
                reviewed <strong>within 24 hours</strong>. Content that breaches Section 4 is
                removed and the poster is barred from posting again.
              </li>
              <li>
                <strong>Blocking.</strong> Every comment carries a <strong>Block</strong>{" "}
                control that immediately hides everything from that author for you. You can
                undo this at any time with "Unblock all" at the top of the comments.
              </li>
            </ul>
            <br />
            You can also reach a human directly at{" "}
            <a href="mailto:hello@betweenthenews.com">hello@betweenthenews.com</a> to report
            content or ask for your own comment to be deleted.
          </Section>

          <Section title="6. Your content">
            You keep ownership of what you post. By posting, you grant us a non-exclusive,
            worldwide, royalty-free licence to display, store and remove that content within
            the Service. You confirm that the content is yours to post. We may remove any
            content at our discretion, in particular where it breaches Section 4.
          </Section>

          <Section title="7. Our content">
            Our analyses, the Media Guide and the rest of the Service are our copyright.
            Quotations from other outlets are short extracts — headlines and single sentences
            — reproduced with attribution for the purpose of criticism, comment and review;
            we do not republish other outlets' articles, photographs or video. If you are a
            rights-holder with a concern, write to{" "}
            <a href="mailto:hello@betweenthenews.com">hello@betweenthenews.com</a> and we will
            respond promptly.
          </Section>

          <Section title="8. Notifications">
            If you turn on notifications with the bell icon, we send you an alert when a new
            analysis is published. Nothing else is sent. You can turn them off at any time in
            your device settings.
          </Section>

          <Section title="9. No warranty">
            The Service is provided "as is". Our analyses are editorial opinion and are not
            legal, financial or professional advice. We are not responsible for content
            posted by readers, or for the content of any external site we link to.
          </Section>

          <Section title="10. Changes and contact">
            We may update these Terms; the date at the top shows when they last changed.
            Questions:{" "}
            <a href="mailto:hello@betweenthenews.com">hello@betweenthenews.com</a>. Privacy:{" "}
            <a href="mailto:privacy@betweenthenews.com">privacy@betweenthenews.com</a> and our{" "}
            <Link to="/privacy">Privacy Policy</Link>.
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: "#15151e", marginBottom: 8 }}>
        {title}
      </h2>
      <div>{children}</div>
    </div>
  );
}

export default TermsOfUse;
